// Plan prices for the UI, read from the same shared file the server charges
// from (builderslab/shared/pricing.json) so the page and the gateway can never
// disagree about what a plan costs.
//
// The bundled table is the fallback used before the server answers. Which
// currency a visitor actually sees is decided server-side from their location
// (GET /api/billing/pricing) — the client cannot pick, because local prices are
// lower and everyone would pick the cheapest one.

import PRICING from '../../builderslab/shared/pricing.json';

export { PRICING };

export const DEFAULT_CURRENCY = PRICING.defaultCurrency;

/** The price table for one currency, falling back to the default. */
export function priceListFor(currency = DEFAULT_CURRENCY) {
  const cfg = PRICING.currencies[String(currency).toUpperCase()]
    || PRICING.currencies[DEFAULT_CURRENCY];
  return {
    currency: cfg.code, symbol: cfg.symbol, locale: cfg.locale, plans: cfg.plans,
    perSite: cfg.perSite, creditPacks: cfg.creditPacks || [],
  };
}

/** Tiers that may buy extra credit packs ('agency' is Studio). */
export const CREDIT_PACK_TIERS = PRICING.creditPackTiers || [];

/** Whether a tier may buy credit packs. */
export function canBuyCreditPacks(tier) {
  return CREDIT_PACK_TIERS.includes(tier);
}

/**
 * A price with a percentage off, rounded the way that currency is charged —
 * the same rule as the server's discountedPrice, so the figure shown at
 * checkout is the figure charged.
 */
export function discountedPrice(amount, currency, rate = 0) {
  const r = Number(rate);
  const base = Number(amount) || 0;
  if (!(r > 0 && r < 1)) return base;
  const raw = base * (1 - r);
  return String(currency || '').toUpperCase() === 'NGN' ? Math.round(raw) : Math.round(raw * 100) / 100;
}

/**
 * Format an amount for display.
 *
 * Naira is shown without decimals — ₦15,000 rather than ₦15,000.00, which is
 * how prices are written locally and keeps the larger numbers readable. Minor
 * units are kept for currencies whose prices genuinely use them.
 */
export function formatPrice(amount, { currency = DEFAULT_CURRENCY, symbol, locale } = {}) {
  const value = Number(amount) || 0;
  const list = priceListFor(currency);
  const sym = symbol ?? list.symbol;
  const loc = locale ?? list.locale;

  // Whole numbers never show ".00"; a price with real minor units keeps them.
  const decimals = Number.isInteger(value) ? 0 : 2;

  try {
    return sym + value.toLocaleString(loc, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  } catch {
    return `${sym}${value}`;
  }
}

/** Monthly-equivalent price when paying yearly, for the "billed annually" line. */
export function monthlyEquivalent(yearly) {
  return Math.round((Number(yearly) || 0) / 12);
}
